(() => {
  const CPB_DEBUG = true;
  const cpbLog = (...args) => CPB_DEBUG && console.log('[CPB]', ...args);
  const cpbWarn = (...args) => CPB_DEBUG && console.warn('[CPB]', ...args);
  const cpbError = (...args) => CPB_DEBUG && console.error('[CPB]', ...args);

  cpbLog('script loaded', { href: window.location.href, readyState: document.readyState });

  let cartCache = null;

  primeCartCache();

  if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.cartData?.items) {
        cartCache = event.cartData;
        cpbLog('cart cache updated from cartUpdate event', { item_count: cartCache.item_count });
      } else {
        primeCartCache();
      }
    });
  }

  document.documentElement.addEventListener('cart:updated', (event) => {
    if (event?.detail?.cart?.items) {
      cartCache = event.detail.cart;
      cpbLog('cart cache updated from cart:updated event', { item_count: cartCache.item_count });
    }
  });

  async function primeCartCache() {
    try {
      cartCache = await getCart();
      cpbLog('initial cart snapshot', {
        item_count: cartCache.item_count,
        items: cartCache.items.map((item) => ({
          key: item.key,
          product_type: item.product_type,
          frsId: getFrsId(item),
          properties: item.properties
        }))
      });
    } catch (err) {
      cpbError('could not prime cart cache', err);
    }
  }

  document.addEventListener('click', onRemoveClick, true);

  async function onRemoveClick(e) {
    const hit = e.target.closest?.('line-item__remove-button, .line-item__remove-button');
    if (!hit) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    const removeBtn = hit.closest('line-item__remove-button') || hit;
    const animatedLineItem = getAnimatedLineItem(removeBtn);
    cpbLog('remove button clicked', removeBtn);

    let cart = cartCache;
    try {
      cart = await getCart();
      cartCache = cart;
      cpbLog('cart snapshot refreshed before remove', { item_count: cartCache.item_count });
    } catch (err) {
      if (!cart?.items) {
        cpbError('cart fetch failed', err);
        return;
      }

      cpbWarn('using stale cart cache after refresh failure', err);
    }

    const line = getLineIndex(removeBtn);
    const clickedItem = getClickedCartItem(removeBtn, cart, line);
    if (!clickedItem?.key) {
      cpbWarn('could not resolve clicked cart item', { line });
      return;
    }

    const frsId = getFrsId(clickedItem);
    cpbLog('clicked line resolved', {
      line,
      clickedKey: clickedItem.key,
      frsId,
      pairs: cart.items.map((item) => ({ key: item.key, frsId: getFrsId(item) }))
    });

    let keys = [clickedItem.key];
    if (frsId) {
      const grouped = cart.items
        .filter((item) => getFrsId(item) === frsId)
        .map((item) => item.key)
        .filter(Boolean);
      if (grouped.length > 1) keys = grouped;
      cpbLog('bundle keys to remove', { frsId, keys });
    } else {
      cpbLog('no _cpb_frs_id -> single remove', { key: clickedItem.key });
    }

    try {
      dispatchLineItemChangeStart(animatedLineItem);

      const updatedCart = await removeCartItems(keys);
      cartCache = updatedCart;
      cpbLog('removed', { keys, item_count: updatedCart?.item_count });

      dispatchCartEvents(updatedCart, {
        replacementDelay: keys.length > 1 || updatedCart.item_count === 0 ? 600 : 300,
        openMiniCart: false
      });
      dispatchLineItemChangeEnd(animatedLineItem, updatedCart, 0);
      await waitForCartUpdate(keys);

      await refreshCartUI(removeBtn);
    } catch (err) {
      dispatchLineItemChangeEnd(animatedLineItem);
      cpbError('remove failed', err);
    }
  }

  function getLineIndex(removeBtn) {
    const href = removeBtn.getAttribute?.('href');
    if (href) {
      try {
        const url = new URL(href, window.location.origin);
        const hrefLine = Number.parseInt(url.searchParams.get('line') ?? '', 10);
        if (Number.isFinite(hrefLine) && hrefLine > 0) return hrefLine;
      } catch {
        cpbWarn('could not parse remove button href', { href });
      }
    }

    const dataIndex = Number.parseInt(removeBtn.getAttribute?.('data-index') ?? '', 10);
    if (Number.isFinite(dataIndex) && dataIndex > 0) return dataIndex;

    const idFromId = Number.parseInt(String(removeBtn.id || '').replace(/\D+/g, ''), 10);
    if (Number.isFinite(idFromId) && idFromId > 0) return idFromId;

    const cartRoot = removeBtn.closest('cart-items, cart-drawer-items') || document;
    const lineItem = removeBtn.closest('.cart-item, .line-item, tr, li');
    if (lineItem) {
      const items = Array.from(cartRoot.querySelectorAll('.cart-item, .line-item'));
      const idx = items.indexOf(lineItem);
      if (idx >= 0) return idx + 1;
    }

    return null;
  }

  function getClickedCartItem(removeBtn, cart, line) {
    const bundleId = normalizeFrsId(
      removeBtn.closest('[data-cpb-custom-bundle]')?.getAttribute('data-cpb-custom-bundle')
    );
    const cartKey = removeBtn.closest('[data-cart-key]')?.getAttribute('data-cart-key');

    if (cartKey) {
      const byKey = cart.items.find((item) => item.key === cartKey);
      if (byKey) return byKey;
    }

    if (bundleId) {
      const byBundle = cart.items.find((item) => getFrsId(item) === bundleId);
      if (byBundle) return byBundle;
    }

    if (line && cart.items?.[line - 1]) {
      return cart.items[line - 1];
    }

    if (bundleId) {
      const normalizedBundleId = normalizeFrsId(bundleId);
      const byLooseBundle = cart.items.find((item) => {
        const itemFrsId = normalizeFrsId(getFrsId(item));
        return itemFrsId && (itemFrsId === normalizedBundleId || itemFrsId.includes(normalizedBundleId) || normalizedBundleId.includes(itemFrsId));
      });
      if (byLooseBundle) return byLooseBundle;
    }

    return line ? cart.items[line - 1] : null;
  }

  function getAnimatedLineItem(removeBtn) {
    const lineItem = removeBtn.closest('line-item');
    if (lineItem instanceof HTMLElement) return lineItem;

    const tableLineItem = removeBtn.closest('tr.line-item');
    return tableLineItem instanceof HTMLElement ? tableLineItem : null;
  }

  function normalizeFrsId(value) {
    if (value == null) return '';
    const str = String(value).trim().replace(/^['"]|['"]$/g, '').trim();
    try {
      return decodeURIComponent(str);
    } catch {
      return str;
    }
  }

  function getFrsId(item) {
    const properties = item?.properties;
    if (!properties || typeof properties !== 'object') return '';

    if (Object.prototype.hasOwnProperty.call(properties, '_cpb_frs_id')) {
      return normalizeFrsId(properties._cpb_frs_id);
    }

    const frsEntry = Object.entries(properties).find(([key]) => {
      const normalizedKey = key?.toLowerCase?.().trim?.();
      return normalizedKey === '_cpb_frs_id' || normalizedKey?.includes('cpb_frs');
    });
    return normalizeFrsId(frsEntry?.[1]);
  }

  function dispatchCartEvents(cartData, refreshDetail = {}) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { cart: cartData }
    }));

    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { cart: cartData, ...refreshDetail }
    }));

    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cpb-remove', cartData });
    }
  }

  function dispatchLineItemChangeStart(lineItem) {
    if (!lineItem) return;

    if (lineItem.tagName.toLowerCase() !== 'line-item') {
      toggleMainCartLoader(lineItem, true);
      return;
    }

    lineItem.dispatchEvent(new CustomEvent('line-item-quantity:change:start', {
      bubbles: true,
      detail: { newLineQuantity: 0 }
    }));
  }

  function dispatchLineItemChangeEnd(lineItem, cartData = null, newLineQuantity = 0) {
    if (!lineItem) return;

    if (lineItem.tagName.toLowerCase() !== 'line-item') {
      if (!cartData) toggleMainCartLoader(lineItem, false);
      return;
    }

    lineItem.dispatchEvent(new CustomEvent('line-item-quantity:change:end', {
      bubbles: true,
      detail: { cart: cartData, newLineQuantity }
    }));
  }

  function toggleMainCartLoader(lineItem, isLoading) {
    const loader = lineItem.querySelector('.line-item__loader');
    const image = lineItem.querySelector('.line-item__image');
    const spinner = loader?.querySelector('.line-item__loader-spinner');
    const mark = loader?.querySelector('.line-item__loader-mark');

    if (!loader || !spinner) return;

    loader.hidden = !isLoading;
    spinner.hidden = !isLoading;
    if (mark) mark.hidden = true;

    if (image) {
      image.style.opacity = isLoading ? '0.35' : '';
    }
  }

  async function getCart() {
    const response = await fetch(`/cart.js?_=${Date.now()}`, {
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!response.ok) throw new Error(`cart.js failed: ${response.status}`);
    return response.json();
  }

  async function removeCartItems(keys) {
    const updates = {};
    for (const key of keys) updates[key] = 0;
    cpbLog('removeCartItems request', { updates });

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates,
        sections: ['mini-cart']
      }),
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) throw new Error(`cart/update.js failed: ${response.status}`);
    return response.json();
  }

  async function waitForCartUpdate(removedKeys, maxAttempts = 5, delay = 250) {
    for (let i = 0; i < maxAttempts; i++) {
      const res = await fetch(`/cart.js?_=${Date.now()}`, {
        credentials: 'same-origin',
        cache: 'no-store'
      });
      const cart = await res.json();
      const remaining = cart.items.filter((item) => removedKeys.includes(item.key));
      if (remaining.length === 0) return;
      await new Promise((r) => setTimeout(r, delay));
    }
    cpbWarn('waitForCartUpdate: max attempts reached');
  }

  async function refreshCartUI(removeBtn) {
    const insideDrawer = !!removeBtn.closest('cart-drawer, .mini-cart, .drawer');

    if (window.themeVariables?.settings?.pageType === 'cart') {
      await rerenderCartSections();
      return;
    }

    if (!insideDrawer) {
      await rerenderCartSections();
    }
  }

  async function rerenderCartSections() {
    cpbLog('rerendering main cart page');

    const cartHtml = await fetch(`/cart?_=${Date.now()}`, {
      credentials: 'same-origin',
      cache: 'no-store'
    }).then((r) => r.text());

    const cartDoc = new DOMParser().parseFromString(cartHtml, 'text/html');
    const currentContainer = document.querySelector('#main .container.container--medium');
    const freshContainer = cartDoc.querySelector('#main .container.container--medium');

    if (currentContainer && freshContainer) {
      currentContainer.replaceWith(freshContainer);
      return;
    }

    const currentMain = document.getElementById('main');
    const freshMain = cartDoc.getElementById('main');

    if (currentMain && freshMain) {
      currentMain.innerHTML = freshMain.innerHTML;
    } else {
      cpbWarn('cart page could not be refreshed');
    }
  }
})();
